import * as React from "react";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { LoadingDots } from "web-ui";

import { getCrashesVersions } from "api/dashboardBackend";
import DuoErrorScreen, { DuoErrorType } from "components/DuoErrorScreen";
import TopbarWithCloseButton from "components/common/TopbarWithCloseButton";
import CrashesContent from "components/crashes/CrashesContent";
import styles from "components/crashes/CrashesPage.module.scss";
import CrashesVersionSelect from "components/crashes/CrashesVersionSelect";
import { PlatformId, isPlatformId } from "util/platform";
import { semVerString } from "util/version";

const CrashesPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const platform = params.platform;
  const version = params.version ?? undefined;
  const { data, error, isLoading } = useQuery({
    enabled: !!(
      platform &&
      isPlatformId(platform) &&
      platform === PlatformId.Android
    ),
    queryFn: () =>
      getCrashesVersions(
        platform && isPlatformId(platform) ? platform : PlatformId.Android,
      ),
    queryKey: [platform],
  });

  const versionChangeCallBack = (newVersion: string, replace = false) => {
    const encodedVersion = encodeURIComponent(newVersion);
    navigate(`/crashes/${platform}/${encodedVersion}`, { replace });
  };

  if (platform !== PlatformId.Android) {
    return (
      <DuoErrorScreen
        error={Error(
          `Unsupported platform: ${platform}. Only Android is supported.`,
        )}
        errorType={DuoErrorType.UnknownError}
      />
    );
  }

  if (error) {
    return (
      <DuoErrorScreen error={error} errorType={DuoErrorType.UnknownError} />
    );
  }

  if (version === undefined || isLoading) {
    if (data !== undefined && data.length > 0) {
      // We want to replace the current entry in the history stack so that we
      // don't end up with an infinite loop when people clicking the back button.
      versionChangeCallBack(semVerString(data[0]), true);
    }
    return <LoadingDots type="screen-white" />;
  }

  return (
    <>
      <TopbarWithCloseButton
        tabs={[
          {
            href: `/history/${platform}`,
            name: "History Releases",
          },
          {
            href: `/crashes/${platform}`,
            isActive: true,
            name: "Crashes",
          },
        ]}
      />
      <div className={styles.queryContainer}>
        <CrashesVersionSelect
          onChangeCallback={versionChangeCallBack}
          platformId={platform}
          queryPrompt="Version:"
          value={version ?? "Loading..."}
        />
      </div>

      <div className={styles.contentContainer}>
        <span className={styles.disclaimer}>
          Note: For older versions, please see{" "}
          <a href="https://docs.google.com/spreadsheets/d/1rExtRTciWPhY2eTmEWkO5PtiGBMeQuLx5Dl1HyliqCE/edit?usp=sharing">
            this spreadsheet
          </a>
        </span>
        <CrashesContent platformId={platform} version={version} />
      </div>
    </>
  );
};

export default CrashesPage;
